import api from "./api";

/* Prestação de contas (fechamento) do processo — espelha CVF.Domain.Services.Settlement */

export type SettlementKind = "expense" | "advance";

export interface SettlementCategory { name: string; kind: SettlementKind }

export interface SettlementIssuer {
  processType: number;
  name?: string | null;
  street?: string | null;
  zipCity?: string | null;
  cnpj?: string | null;
  bank?: string | null;
  agency?: string | null;
  account?: string | null;
  pix?: string | null;
  beneficiary?: string | null;
}

export interface SettlementEmailSettings {
  toClientContacts: boolean;
  cc?: string | null;
  subject: string;
  bodyCharge: string;
  bodyRefund: string;
  dueDays: number;
}

export interface SettlementSettings {
  categories: SettlementCategory[];
  issuers: SettlementIssuer[];
  email: SettlementEmailSettings;
}

export interface SettlementLine {
  key: string;
  entryId?: string | null;
  sourceType: "financial" | "numerary" | "manual";
  sourceId?: string | null;
  kind: SettlementKind;
  category?: string | null;
  description: string;
  date?: string | null;
  currency?: string | null;
  foreignAmount?: number | null;
  rate?: number | null;
  amountBrl?: number | null;
  paid: boolean;
  paidBySource: boolean;
  documentId?: string | null;
  documentName?: string | null;
  excluded: boolean;
  notes?: string | null;
  manual: boolean;
}

export interface SettlementDocumentOption { id: string; name: string }

export interface Settlement {
  processId: string;
  cvfRef: string;
  clientName?: string | null;
  clientRef?: string | null;
  modality: string;
  issuer?: SettlementIssuer | null;
  expenses: SettlementLine[];
  advances: SettlementLine[];
  totalExpenses: number;
  totalAdvances: number;
  balance: number;
  balanceDirection: "charge" | "refund" | "zero";
  missingReceipts: number;
  unpaid: number;
  withoutValue: number;
  documents: SettlementDocumentOption[];
  categories: SettlementCategory[];
  lastSentAt?: string | null;
  lastSentBy?: string | null;
}

export interface SettlementEntryInput {
  kind: SettlementKind;
  category?: string | null;
  description?: string | null;
  amountBrl?: number | null;
  date?: string | null;
  paid?: boolean | null;
  documentId?: string | null;
  notes?: string | null;
}

export interface SettlementAdjustInput {
  sourceType: string;
  sourceId: string;
  paid?: boolean | null;
  documentId?: string | null;
  clearDocument?: boolean;
  excluded?: boolean | null;
}

export interface SettlementEmailPreview {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  dueDate?: string | null;
  linkedDocuments: SettlementDocumentOption[];
  otherDocuments: SettlementDocumentOption[];
}

export interface SettlementSendInput {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  attachPdf: boolean;
  attachExcel: boolean;
  documentIds: string[];
}

const base = (id: string) => `/api/process/${id}/settlement`;

const errorsOf = (e: any, fallback: string) => {
  const errs: string[] | undefined = e?.response?.data?.errors;
  return new Error(Array.isArray(errs) && errs.length ? errs.join("\n") : fallback);
};

export const getSettlement = async (id: string): Promise<Settlement> => (await api.get(base(id))).data;

export const saveSettlementEntry = async (id: string, entryId: string | null, input: SettlementEntryInput): Promise<Settlement> => {
  try {
    return (entryId ? await api.put(`${base(id)}/entries/${entryId}`, input) : await api.post(`${base(id)}/entries`, input)).data;
  } catch (e) { throw errorsOf(e, "Não foi possível salvar o lançamento."); }
};

export const deleteSettlementEntry = async (id: string, entryId: string): Promise<Settlement> => {
  try { return (await api.delete(`${base(id)}/entries/${entryId}`)).data; }
  catch (e) { throw errorsOf(e, "Não foi possível excluir o lançamento."); }
};

export const adjustSettlementLine = async (id: string, input: SettlementAdjustInput): Promise<Settlement> => {
  try { return (await api.put(`${base(id)}/adjust`, input)).data; }
  catch (e) { throw errorsOf(e, "Não foi possível atualizar a linha."); }
};

export const downloadSettlement = async (id: string, kind: "pdf" | "excel") => {
  const response = await api.get(`${base(id)}/${kind}`, { responseType: "blob" });
  const disposition = response.headers["content-disposition"] ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const name = encoded ? decodeURIComponent(encoded) : plain ?? `prestacao-de-contas.${kind === "pdf" ? "pdf" : "xlsx"}`;
  const url = URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const getSettlementEmail = async (id: string): Promise<SettlementEmailPreview> => (await api.get(`${base(id)}/email`)).data;

export const sendSettlement = async (id: string, input: SettlementSendInput) => {
  try { return (await api.post(`${base(id)}/send`, input)).data; }
  catch (e) { throw errorsOf(e, "Não foi possível enviar a prestação de contas."); }
};

export const getSettlementSettings = async (): Promise<SettlementSettings> => (await api.get("/api/settlement/settings")).data;

export const saveSettlementSettings = async (settings: SettlementSettings): Promise<SettlementSettings> => {
  try { return (await api.put("/api/settlement/settings", settings)).data; }
  catch (e) { throw errorsOf(e, "Não foi possível salvar a configuração."); }
};
